/**
 * WizardStepInstruments — Step 3: Capture current InstrumentForge / Production config.
 * Reads from ProductionConfigContext.
 */

import React, { useState } from 'react';
import { Button, Badge, TextArea } from '@components/ui';
import { Guitar, Check, SkipForward, PenLine } from 'lucide-react';
import { useProductionConfig } from '@context/ProductionConfigContext';

interface WizardStepInstrumentsProps {
  instrumentConfigJson: string | null;
  productionArchitecture: string;
  onCapture: (configJson: string, architecture: string) => void;
  onSkip: () => void;
}

export const WizardStepInstruments: React.FC<WizardStepInstrumentsProps> = ({
  instrumentConfigJson,
  productionArchitecture,
  onCapture,
  onSkip,
}) => {
  const { getFullProductionArchitecture, config, instrumentForgeConfig } = useProductionConfig();
  const [manualMode, setManualMode] = useState(false);
  const [manualText, setManualText] = useState(productionArchitecture);
  const isCaptured = productionArchitecture.length > 0;

  const handleCapture = () => {
    const arch = getFullProductionArchitecture() || '';
    const configData = instrumentForgeConfig ? JSON.stringify(instrumentForgeConfig) : 'null';
    onCapture(configData, arch);
  };

  const handleManualSave = () => {
    // No InstrumentForge config to attach — architecture text is hand-typed, config JSON stays null
    onCapture('null', manualText);
    setManualMode(false);
  };

  // Preview current config
  const coreCount = config?.instrumentStack?.core?.length || 0;
  const colorCount = config?.instrumentStack?.color?.length || 0;
  const textureCount = config?.instrumentStack?.texture?.length || 0;
  const hasKey = !!config?.selectedKey;
  const hasTempo = !!config?.selectedTempo;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-400">
        Capture your current Production Intelligence and InstrumentForge configuration,
        or type/paste a production architecture directly.
      </p>

      {!manualMode && (
        <>
          {/* Current config preview */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-800">
            <div className="flex items-center gap-2 mb-3">
              <Guitar size={16} className="text-slate-500" />
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Current Production Config</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {hasKey && <Badge variant="primary">{config.selectedKey!.root} {config.selectedKey!.mode}</Badge>}
              {hasTempo && <Badge variant="primary">{config.selectedTempo} BPM</Badge>}
              {coreCount > 0 && <Badge variant="info">{coreCount} Core</Badge>}
              {colorCount > 0 && <Badge variant="info">{colorCount} Color</Badge>}
              {textureCount > 0 && <Badge variant="info">{textureCount} Texture</Badge>}
              {config?.mixTemplate && <Badge variant="default-muted">Mix: {config.mixTemplate.name}</Badge>}
              {instrumentForgeConfig && <Badge variant="success">InstrumentForge</Badge>}
            </div>
          </div>

          {/* Capture status */}
          {isCaptured ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-green-950/20 border border-green-900/30 rounded-xl">
                <Check size={16} className="text-green-400" />
                <span className="text-sm text-green-300 font-bold">
                  {instrumentConfigJson && instrumentConfigJson !== 'null' ? 'Production config captured' : 'Manual production architecture entered'}
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">{productionArchitecture.length} chars</span>
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Guitar size={14} />} onClick={handleCapture}>
                Re-capture from Current Config
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="primary"
                size="sm"
                fullWidth
                leftIcon={<Guitar size={14} />}
                onClick={handleCapture}
              >
                Capture Current Production Config
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<SkipForward size={14} />}
                onClick={onSkip}
              >
                Skip
              </Button>
            </div>
          )}

          <button
            onClick={() => { setManualText(productionArchitecture); setManualMode(true); }}
            className="text-xs text-slate-500 hover:text-amber-400 transition-colors flex items-center gap-1.5"
          >
            <PenLine size={12} /> {isCaptured && (!instrumentConfigJson || instrumentConfigJson === 'null') ? 'Edit manual entry' : 'Type it in manually instead'}
          </button>
        </>
      )}

      {manualMode && (
        <div className="space-y-3">
          <TextArea
            value={manualText}
            onChange={e => setManualText(e.target.value)}
            placeholder="Describe the production architecture directly — key, tempo, instrument stack, mix character, genre DNA, etc."
            rows={10}
            className="!bg-slate-900 !border-slate-700 font-mono text-xs"
          />
          <div className="flex gap-3">
            <Button variant="primary" size="sm" leftIcon={<Check size={14} />} onClick={handleManualSave} disabled={!manualText.trim()}>
              Save Manual Entry
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setManualMode(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
